import { ImportTargetColumn } from '../types/import-target-column.type'

export const CONTACT_FIELD_NAMES = new Set(['email', 'phone', 'phone_number'])

export const DEFAULT_IMPORT_TARGET_COLUMNS: ImportTargetColumn[] = [
  { field: 'full_name', header: 'Full name' },
  { field: 'email', header: 'E-mail' },
  { field: 'phone', header: 'Phone number' }
]

/**
 * Collapses a column name to lowercase alphanumeric characters for matching.
 * @param value {string} - Field, header, or CSV column name.
 * @returns Match key.
 */
function toColumnMatchKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '')
}

/**
 * Finds the best matching persona field for a CSV header.
 * Order: exact field/header → known aliases → includes similar terms (e.g. "job" → "job_title").
 * @param sourceCol {string} - The source column.
 * @param targetColumns {ImportTargetColumn[]} - The target columns.
 * @returns The best matching persona field.
 */
export function findMatchingTargetColumn(sourceCol: string, targetColumns: ImportTargetColumn[]): ImportTargetColumn | undefined {
  const sourceKey = toColumnMatchKey(sourceCol)
  if (!sourceKey) return undefined

  const exactMatch = targetColumns.find((target) => {
    return toColumnMatchKey(target.field) === sourceKey || toColumnMatchKey(target.header) === sourceKey
  })
  if (exactMatch) return exactMatch

  const lowerCaseSourceCol = sourceCol.trim().toLowerCase()
  switch (true) {
    case lowerCaseSourceCol.includes('full') && lowerCaseSourceCol.includes('name'):
    case lowerCaseSourceCol === 'fullname':
    case lowerCaseSourceCol === 'name':
      return targetColumns.find((target) => target.field === 'full_name' || target.field === 'fullname')
    case lowerCaseSourceCol.includes('first') && lowerCaseSourceCol.includes('name'):
      return targetColumns.find((target) => target.field === 'first_name')
    case lowerCaseSourceCol.includes('last') && lowerCaseSourceCol.includes('name'):
      return targetColumns.find((target) => target.field === 'last_name')
    case lowerCaseSourceCol.includes('email') || lowerCaseSourceCol.includes('mail'):
      return targetColumns.find((target) => target.field === 'email')
    case lowerCaseSourceCol.includes('phone'):
      return targetColumns.find((target) => target.field === 'phone' || target.field === 'phone_number')
  }

  if (sourceKey.length < 3) return undefined

  return targetColumns.find((target) => {
    const fieldKey = toColumnMatchKey(target.field)
    const headerKey = toColumnMatchKey(target.header)
    return fieldKey.includes(sourceKey) || sourceKey.includes(fieldKey) || headerKey.includes(sourceKey) || sourceKey.includes(headerKey)
  })
}

/**
 * Builds initial field -> CSV column mappings from file headers.
 * @param fileColumns {string[]} - The file columns.
 * @param targetColumns {ImportTargetColumn[]} - The target columns.
 * @returns The initial field -> CSV column mappings.
 */
export function buildAutoColumnMappings(fileColumns: string[], targetColumns: ImportTargetColumn[]): Record<string, string> {
  const mappings: Record<string, string> = {}
  const usedTargets = new Set<string>()

  fileColumns.forEach((sourceCol) => {
    const matchingTarget = findMatchingTargetColumn(sourceCol, targetColumns)
    if (!matchingTarget || usedTargets.has(matchingTarget.field)) return

    usedTargets.add(matchingTarget.field)
    mappings[matchingTarget.field] = sourceCol
  })

  return mappings
}

/**
 * Returns true when at least one contact field is mapped to a CSV column.
 * @param targetColumns {ImportTargetColumn[]} - The target columns.
 * @param mappings {Record<string, string>} - The mappings.
 * @returns Boolean indicating if at least one contact field is mapped to a CSV column.
 */
export function hasRequiredContactFieldMapped(targetColumns: ImportTargetColumn[], mappings: Record<string, string>): boolean {
  return targetColumns.some((column) => {
    if (!CONTACT_FIELD_NAMES.has(column.field)) return false
    return !!mappings[column.field]?.trim()
  })
}

/**
 * Builds API payload mapping: CSV column name -> persona field name.
 * @param mappings {Record<string, string>} - The mappings.
 * @returns The API payload mapping.
 */
export function buildImportDataMapping(mappings: Record<string, string>): Record<string, string> {
  return Object.entries(mappings).reduce<Record<string, string>>((payload, [targetField, sourceColumn]) => {
    const trimmedSource = sourceColumn.trim()
    if (trimmedSource) payload[trimmedSource] = targetField
    return payload
  }, {})
}

const INVITATION_COLLECTION_FIELD_ALIASES: Record<string, string> = {
  full_name: 'fullname',
  fullname: 'fullname',
  email: 'email',
  phone: 'phone',
  phone_number: 'phone'
}

/**
 * Builds invitation-collection data_mapping for all mapped fields.
 * Known contact aliases are remapped (e.g. full_name → fullname, phone_number → phone);
 * every other mapped field is passed through as returned from the mapping UI.
 * @param dataMapping {Record<string, string>} - CSV column name -> target field name.
 * @returns Mapping with normalized invitee field names for all mapped columns.
 */
export function buildInvitationCollectionDataMapping(dataMapping: Record<string, string>): Record<string, string> {
  return Object.entries(dataMapping).reduce<Record<string, string>>((payload, [sourceColumn, targetField]) => {
    const trimmedSource = sourceColumn.trim()
    const trimmedTarget = targetField.trim()
    if (!trimmedSource || !trimmedTarget) return payload

    payload[trimmedSource] = INVITATION_COLLECTION_FIELD_ALIASES[trimmedTarget] ?? trimmedTarget
    return payload
  }, {})
}

/**
 * Counts the number of mapped columns.
 * @param mappings {Record<string, string>} - The mappings.
 * @returns The number of mapped columns.
 */
export function countMappedColumns(mappings: Record<string, string>): number {
  return Object.values(mappings).filter((sourceColumn) => sourceColumn.trim() !== '').length
}
